import { useEffect, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from "@mui/icons-material/Download";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "../../../examples/Tables/DataTable";
import data from "../../dashboard/components/Projects/data";
import useApi from "../../../components/Utils/api";
import toast from "react-hot-toast";
import { DatePicker } from "rsuite";
import { afterToday } from "rsuite/cjs/DateRangePicker/disabledDateUtils";
import Papa from 'papaparse';
import TransactionsDialog from "./TransactionsDialog";

function SettlementReport() {
  const { settlementReportsColumns } = data();
  const [summary, setSummary] = useState([]);
  const [openTransactionsDialog, setOpenTransactionsDialog] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);

  // Function to get today's date at midnight in AEST timezone
  const getTodayDateAtMidnight = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Set to start of the day in local time
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000); // Convert to local midnight in AEST
  };

  // Initialize selectedDate to today's date at local midnight
  const [selectedDate, setSelectedDate] = useState(
    getTodayDateAtMidnight().toISOString().slice(0, 10)
  );
  
  const api = useApi();

  // Format date to 'yyyy-MM-dd' and adjust to AEST timezone
  const formatDate = (date) => {
    if (!(date instanceof Date) || isNaN(date.getTime())) {
      console.error("Invalid date provided:", date);
      return '';
    }
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
  };

  const handleViewTransactions = (settlement) => {
    setSelectedSettlement(settlement);
    setOpenTransactionsDialog(true);
  };

  const handleCloseTransactionsDialog = () => {
    setOpenTransactionsDialog(false);
    setSelectedSettlement(null);
  };

  const handleDownloadBatch = (batchId) => {
    const data = {
      batchId: batchId
    };

    api
      .post("/adminPortal/reports/settlement/download-batch", data, {
        responseType: 'blob'
      })
      .then((response) => {
        // Create a blob from the response data
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        
        const fileName = `Settlement_Batch_${batchId}.csv`;
        
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        
        toast.success("Batch downloaded successfully");
      })
      .catch((error) => {
        // Authorization error
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }

        // Server / network error
        const toastId = toast.error("Failed to download batch. Please try again later.", {
          autoClose: false,
        });
        setTimeout(() => toast.dismiss(toastId), 1000);
      });
  };

  const groupByBatchId = (data) => {
    const grouped = {};
    data.forEach(item => {
      const batchId = item.batchId || 'No Batch ID';
      if (!grouped[batchId]) {
        grouped[batchId] = [];
      }
      grouped[batchId].push(item);
    });
    return grouped;
  };

  const getSettlementReport = (date) => {
    if (!date) return;

    const selectedDateObj = new Date(date);

    const data = {
      date: formatDate(selectedDateObj),
    };

    api
      .post("/adminPortal/reports/settlement", data)
      .then((response) => {
        const { responseCode, data, message } = response.data || {};

        // Success but no records
        if (responseCode === 200 && (!data || data.length === 0)) {
          setSummary([]);
          return;
        }

        // Success with data
        if (responseCode === 200) {
          // Sort by batchId to group them together
          const sortedData = [...data].sort((a, b) => 
            (a.batchId || '').localeCompare(b.batchId || '')
          );
          
          // Track first occurrence of each batchId for rowSpan
          const batchFirstIndex = {};
          const batchCounts = {};
          
          sortedData.forEach((item, index) => {
            const batchId = item.batchId || 'No Batch';
            if (!(batchId in batchFirstIndex)) {
              batchFirstIndex[batchId] = index;
              batchCounts[batchId] = 1;
            } else {
              batchCounts[batchId]++;
            }
          });
          
          const modifiedData = sortedData.map((item, index) => {
            const batchId = item.batchId || 'No Batch';
            const isFirstInBatch = batchFirstIndex[batchId] === index;
            const rowSpan = batchCounts[batchId];
            
            return {
              ...item,
              batchId,
              downloadButton_rowSpan: isFirstInBatch ? rowSpan : undefined,
              downloadButton_skip: !isFirstInBatch,
              action: (
                <MDButton
                  variant="contained"
                  size="small"
                  color="info"
                  onClick={() => handleViewTransactions(item)}
                >
                  View Transactions
                </MDButton>
              ),
              downloadButton: isFirstInBatch ? (
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleDownloadBatch(batchId)}
                  title={`Download all records for Batch ${batchId}`}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              ) : null,
            };
          });
          
          setSummary(modifiedData);
          return;
        }

        // Business-level failure
        const toastId = toast.error(message || "Failed to fetch settlement report", {
          autoClose: false,
        });
        setTimeout(() => toast.dismiss(toastId), 1000);
      })
      .catch((error) => {
        // Authorization error
        if (error.response && error.response.status === 403) {
          toast.error("You do not have permission to perform this action.");
          return;
        }

        // Server / network error
        const toastId = toast.error("Something went wrong. Please try again later.", {
          autoClose: false,
        });
        setTimeout(() => toast.dismiss(toastId), 1000);
      });
  };

  const generateCSV = () => {
    if (summary.length === 0) {
      return '';
    }

    const headers = settlementReportsColumns.map(column => column.Header);

    const summaryWithUppercaseColumns = summary.map(item => {
      return settlementReportsColumns.map(column => {
        const accessor = column.accessor;
        const value = typeof accessor === 'function' ? accessor(item) : item[accessor];
        return value;
      });
    });

    const csvData = [headers, ...summaryWithUppercaseColumns];

    const csv = Papa.unparse(csvData, {
      quotes: true,
    });

    return csv;
  };

  const handleDownloadCSV = () => {
    if (summary.length === 0) {
      const toastId = toast.error("No records found to download", { autoClose: false });
      setTimeout(() => toast.dismiss(toastId), 1000);
      return;
    }

    const csvData = generateCSV();

    const currentDate = new Date().toISOString().slice(0, 10);
    const currentTime = new Date().toLocaleTimeString().replace(/:/g, '-');
    const dateTimeString = `${currentDate}_${currentTime}`;

    const fileName = `SettlementReport_${dateTimeString}.csv`;

    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  useEffect(() => {
    if (selectedDate !== null) {
      getSettlementReport(selectedDate);
    }
  }, [selectedDate]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={6} mb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12}>
            <MDBox mb={3} position="relative" display="flex" justifyContent="center">
              <DatePicker
                disabledDate={afterToday()}
                onChange={(date) => {
                  if (date) {
                    setSelectedDate(date);
                  }
                }}
                format="dd MMM yyyy"
                value={selectedDate ? new Date(selectedDate) : null}
                placeholder="Select Date"
              />
              <MDButton
                variant="gradient"
                onClick={handleDownloadCSV}
                color="info"
                style={{ position: "absolute", right: 0, top: 0 }}
              >
                Export
              </MDButton>
            </MDBox>

            <DataTable
              table={{
                columns: [
                  ...settlementReportsColumns,
                  {
                    Header: "Download",
                    accessor: "downloadButton",
                    width: "10%",
                    align: "center",
                    headerSx: { whiteSpace: "nowrap" },
                  }
                ],
                rows: summary
              }}
              showTotalEntries={true}
              isSorted={false}
              noEndBorder
              entriesPerPage={{ defaultValue: 25 }}
              entriesPerPagePosition="bottom-right"
              canSearch={true}
            />
          </Grid>
        </Grid>
      </MDBox>
      
      <TransactionsDialog
        open={openTransactionsDialog}
        onClose={handleCloseTransactionsDialog}
        transactions={selectedSettlement?.transactions || []}
        settlementRecord={selectedSettlement}
      />
      
      <Footer />
    </DashboardLayout>
  );
}

export default SettlementReport;
